'use client';

import { useAtom, useAtomValue } from 'jotai';

import { currentProductIdAtom } from '../../../Atoms/CurrentProductId.atom';
import { selectedProductIdAtom } from '../../../Atoms/SelectedProductId.atom';

import { isPfs, isVwfs, mapperFinanceProducts } from '../Helpers/FinanceMap';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { usePathname } from 'next/navigation';
import { calculationAtom } from '../../../Atoms/Calculation.atom';
import { eligibleProductsAtom } from '../../../Atoms/EligibleProducts.atom';
import { finCalcInteractedWithAtom } from '../../../Atoms/FinCalcInteractedWith.atom';
import { isEmptyPayloadAtom } from '../../../Atoms/isEmptyPayload.atom';
import { isLeasingFinancing } from '../../../Utils/isFinanceCaptive';
import { CalculatorMessages } from '../Calculator.messages';
import styles from './ProductToggle.module.css';

export type ProductProps = {
  strings: CalculatorMessages;
};

export const ProductToggle = ({ strings }: ProductProps) => {
  const [_, setIsEmpty] = useAtom(isEmptyPayloadAtom);
  const locale = useAtomValue(localeAtom);

  const eligibleProducts = useAtomValue(eligibleProductsAtom);
  const [selectedProductId, setSelectedProductId] = useAtom(selectedProductIdAtom);
  const currentProductId = useAtomValue(currentProductIdAtom);
  const [, setFinCalcInteractedWith] = useAtom(finCalcInteractedWithAtom);
  const calculation = useAtomValue(calculationAtom)!;
  const captiveId = calculation?.captiveId;
  const pathname = usePathname();

  const isCurrentVwfs = isVwfs(captiveId);
  const isCurrentPorsche = isPfs(captiveId);
  const hasLeasingFinancing = isLeasingFinancing(calculation);
  const hasVwfsLeasing = hasLeasingFinancing && isCurrentVwfs;
  const handleButtonClick = () => {
    setFinCalcInteractedWith(true);
  };

  const setEmptySelectedProduct = () => {
    setIsEmpty(true);
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const productId = event.target.value;
    setSelectedProductId(productId);
    if (locale === 'fr') {
      setEmptySelectedProduct();
    }
  };

  const getProductTitle = (productId: string) => {
    if (hasVwfsLeasing) return mapperFinanceProducts['vwfsPrivateLeasing']?.title[locale];
    if (isCurrentPorsche && productId === 'privateLeasing') return strings.porscheLeasing;
    return mapperFinanceProducts[productId]?.title[locale];
  };

  if (pathname.includes('/finance-deal')) return null;

  return (
    <>
      <fieldset name="finance products" className={styles.financeOptionsType}>
        {eligibleProducts?.map?.(productId => (
          <div className={styles.financeOption} key={productId} onClick={handleButtonClick}>
            <input
              id={`finance-product-${productId}`}
              name="finance product"
              type="radio"
              value={productId}
              checked={(selectedProductId ?? currentProductId) === productId}
              onChange={handleToggleChange}
            />
            <label htmlFor={`finance-product-${productId}`}>{getProductTitle(productId)}</label>
          </div>
        ))}
      </fieldset>
      {locale === 'uk' && <div className={styles.divider} />}
    </>
  );
};
